import { ROTULO_STATUS, type StatusPedido, TOM_STATUS, type TomStatus } from '@entregas/core';
import { type HTMLAttributes, type ReactNode, useEffect, useId, useRef } from 'react';
import { Link } from 'react-router';
import { Botao } from './Botao';
import css from './blocos.module.css';
import { Icone } from './Icone';
import { Kbd } from './Kbd';

export function Cartao({ className, ...resto }: HTMLAttributes<HTMLDivElement>) {
  return <div className={[css.cartao, className].filter(Boolean).join(' ')} {...resto} />;
}

/** Cartão com cabeçalho (título, descrição e ação à direita): agrupa uma etapa do formulário. */
export function CartaoTitulado({
  titulo,
  descricao,
  acao,
  children,
  className,
}: {
  titulo: string;
  descricao?: ReactNode;
  acao?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <section
      className={[css.cartao, css.titulado, className].filter(Boolean).join(' ')}
      aria-labelledby={id}
    >
      <header className={css.cabecalhoCartao}>
        <div>
          <h2 id={id} className={css.tituloCartao}>
            {titulo}
          </h2>
          {descricao && <p className={css.descricaoCartao}>{descricao}</p>}
        </div>
        {acao}
      </header>
      <div className={css.corpoCartao}>{children}</div>
    </section>
  );
}

export function Selo({ tom, children }: { tom: TomStatus; children: ReactNode }) {
  return <span className={`${css.selo} ${css[`tom-${tom}`]}`}>{children}</span>;
}

export function SeloStatus({ status }: { status: StatusPedido }) {
  return <Selo tom={TOM_STATUS[status]}>{ROTULO_STATUS[status]}</Selo>;
}

export function Pagina({
  titulo,
  selo,
  subtitulo,
  acoes,
  voltar,
  children,
}: {
  titulo: string;
  /** Selo ao lado do título (ex.: o status do pedido); fica fora do h1 para não mudar o nome dele. */
  selo?: ReactNode;
  subtitulo?: ReactNode;
  acoes?: ReactNode;
  /** "← Pedidos Esc" acima do título (o Esc é do useAtalhosGlobais: volta para a mesma rota). */
  voltar?: { para: string; rotulo: string };
  children: ReactNode;
}) {
  return (
    <section className={css.pagina} aria-labelledby="titulo-pagina">
      <header className={css.cabecalho}>
        <div>
          {voltar && (
            <Link to={voltar.para} className={css.voltar} aria-keyshortcuts="Escape">
              <Icone nome="volta" tamanho={15} />
              {voltar.rotulo}
              <Kbd aria-hidden="true">Esc</Kbd>
            </Link>
          )}
          {selo ? (
            <div className={css.tituloComSelo}>
              <h1 id="titulo-pagina" className={css.titulo}>
                {titulo}
              </h1>
              {selo}
            </div>
          ) : (
            <h1 id="titulo-pagina" className={css.titulo}>
              {titulo}
            </h1>
          )}
          {subtitulo && <p className={css.subtitulo}>{subtitulo}</p>}
        </div>
        {acoes && <div className={css.acoes}>{acoes}</div>}
      </header>
      {children}
    </section>
  );
}

export function Carregando({ linhas = 3 }: { linhas?: number }) {
  return (
    <div className={css.listaEsqueleto} aria-busy="true">
      <span className="sr-only">Carregando…</span>
      {Array.from({ length: linhas }, (_, i) => `esqueleto-${i}`).map((chave) => (
        <div key={chave} className={css.esqueleto} />
      ))}
    </div>
  );
}

export function Vazio({ titulo, children }: { titulo: string; children?: ReactNode }) {
  return (
    <div className={css.estado}>
      <p className={css.estadoTitulo}>{titulo}</p>
      {children}
    </div>
  );
}

/** Erro de consulta com "Tentar de novo". A mensagem já vem em pt-BR (ErroApi). */
export function ErroCarga({ erro, onTentar }: { erro: unknown; onTentar?: () => void }) {
  const mensagem = erro instanceof Error ? erro.message : 'Não foi possível carregar.';
  return (
    <div className={css.estado} role="alert">
      <p className={css.estadoTitulo}>Não deu para carregar</p>
      <p>{mensagem}</p>
      {onTentar && (
        <Botao variante="secundario" onClick={onTentar}>
          Tentar de novo
        </Botao>
      )}
    </div>
  );
}

export function Aviso({
  children,
  tom = 'erro',
}: {
  children: ReactNode;
  tom?: 'erro' | 'sucesso';
}) {
  return (
    <div
      className={tom === 'sucesso' ? `${css.aviso} ${css.avisoSucesso}` : css.aviso}
      role={tom === 'sucesso' ? 'status' : 'alert'}
    >
      {children}
    </div>
  );
}

/** Diálogo modal nativo (<dialog>): foco preso, Esc fecha, fundo inerte — sem biblioteca. */
export function Dialogo({
  aberto,
  aoFechar,
  rotulo,
  largo,
  children,
}: {
  aberto: boolean;
  aoFechar: () => void;
  rotulo: string;
  /** Para conteúdo com tabela/gráfico (até 880 px). */
  largo?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialogo = ref.current;
    if (!dialogo) return;
    if (aberto && !dialogo.open) dialogo.showModal();
    if (!aberto && dialogo.open) dialogo.close();
  }, [aberto]);

  return (
    <dialog
      ref={ref}
      className={largo ? `${css.dialogo} ${css.dialogoLargo}` : css.dialogo}
      aria-label={rotulo}
      onClose={aoFechar}
    >
      {aberto && <div className={css.dialogoConteudo}>{children}</div>}
    </dialog>
  );
}
